import axios from 'axios';
import type { Language, Article, Place, Event, Currency, Category, CategoryType, VedurWeatherResponse, OpenExchangeRatesResponse, WordOfDay } from '@/types';
import { WEATHER_API, CURRENCY_API, HISTORICAL_CURRENCY_API, AIR_QUALITY_API, HOLIDAYS_API } from '@/constants/api';
import { addPendingRequest, registerBackgroundSync } from './offline-queue';

const API_BASE = '/api';

const client = axios.create({
  baseURL: API_BASE,
  timeout: 10000,
});

// Client for external APIs (no base URL)
const externalClient = axios.create({
  timeout: 10000,
});

// Add response interceptor for offline error handling
client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;

    // If offline and request failed, save it for later
    if (!navigator.onLine && config && config.method?.toUpperCase() !== 'GET') {
      try {
        const headers = config.headers || {};
        const body = config.data ? JSON.stringify(config.data) : undefined;

        await addPendingRequest(
          `${API_BASE}${config.url}`,
          config.method?.toUpperCase() || 'POST',
          headers as Record<string, string>,
          body,
        );

        // Register background sync
        await registerBackgroundSync();

        return Promise.resolve({ data: { offline: true, cached: true } });
      } catch (queueError) {
        console.error('Failed to queue offline request:', queueError);
      }
    }

    return Promise.reject(error);
  },
);

// Articles API
export const articlesApi = {
  // Get all articles with optional filters
  getAll: async (language: Language, category?: string) => {
    const params = new URLSearchParams({ lang: language });
    if (category) params.append('category', category);

    const response = await client.get<Article[]>(`/articles?${params}`);
    return response.data;
  },

  // Get single article by ID
  getById: async (id: string, language: Language) => {
    const response = await client.get<Article>(`/articles/${id}?lang=${language}`);
    return response.data;
  },

  // Get featured articles
  getFeatured: async (language: Language) => {
    const response = await client.get<Article[]>(`/articles?lang=${language}&featured=true`);
    return response.data;
  },

  // Get articles by category
  getByCategory: async (language: Language, category: string) => {
    const response = await client.get<Article[]>(`/articles?lang=${language}&category=${category}`);
    return response.data;
  },

  // Get related articles by IDs
  getRelated: async (articleId: string, language: Language) => {
    const response = await client.get<Article[]>(`/articles/${articleId}/related?lang=${language}`);
    return response.data;
  },

  // Get articles by ID list
  getByIds: async (ids: string[], language: Language) => {
    if (ids.length === 0) return [];

    const allArticles = await articlesApi.getAll(language);
    return allArticles.filter(article => ids.includes(article.id));
  },
};

// Places API
export const placesApi = {
  // Get all places with optional filters
  getAll: async (language: Language, category?: string) => {
    const params = new URLSearchParams({ lang: language });
    if (category) params.append('category', category);

    const response = await client.get<Place[]>(`/places?${params}`);
    return response.data;
  },

  // Get single place by ID
  getById: async (id: string, language: Language) => {
    const response = await client.get<Place>(`/places/${id}?lang=${language}`);
    return response.data;
  },

  // Get promoted places
  getPromoted: async (language: Language) => {
    const response = await client.get<Place[]>(`/places?lang=${language}&promoted=true`);
    return response.data;
  },

  // Get places by category
  getByCategory: async (language: Language, category: string) => {
    const response = await client.get<Place[]>(`/places?lang=${language}&category=${category}`);
    return response.data;
  },
};

// Categories API
export const categoriesApi = {
  // Get all categories
  getAll: async (language: Language) => {
    const response = await client.get<Category[]>(`/categories?lang=${language}`);
    return response.data;
  },

  // Get categories by content type (articles, events, places)
  getByType: async (language: Language, type: CategoryType) => {
    const response = await client.get<Category[]>(
      `/categories?lang=${language}&type=${type}`
    );
    return response.data;
  },

  // Get homepage categories (showOnHomepage=true)
  getHomepage: async (language: Language) => {
    const response = await client.get<Category[]>(
      `/categories?lang=${language}&homepage=true`
    );
    return response.data;
  },

  // Get category by slug
  getBySlug: async (slug: string, language: Language) => {
    const response = await client.get<Category>(`/categories/${slug}?lang=${language}`);
    return response.data;
  },
};

// Events API
export const eventsApi = {
  // Get all events with optional filters
  getAll: async (language: Language, status?: 'upcoming' | 'past', category?: string) => {
    const params = new URLSearchParams({ lang: language });
    if (status) params.append('status', status);
    if (category) params.append('category', category);

    const response = await client.get<Event[]>(`/events?${params}`);
    return response.data;
  },

  // Get single event by ID
  getById: async (id: string, language: Language) => {
    const response = await client.get<Event>(`/events/${id}?lang=${language}`);
    return response.data;
  },

  // Get upcoming events
  getUpcoming: async (language: Language, category?: string) => {
    const params = new URLSearchParams({ lang: language, status: 'upcoming' });
    if (category) params.append('category', category);

    const response = await client.get<Event[]>(`/events?${params}`);
    return response.data;
  },

  // Get past events (archive)
  getPast: async (language: Language, category?: string) => {
    const params = new URLSearchParams({ lang: language, status: 'past' });
    if (category) params.append('category', category);

    const response = await client.get<Event[]>(`/events?${params}`);
    return response.data;
  },

  // Get featured events
  getFeatured: async (language: Language) => {
    const response = await client.get<Event[]>(`/events?lang=${language}&featured=true`);
    return response.data;
  },

  // Get events by category
  getByCategory: async (language: Language, category: string) => {
    const response = await client.get<Event[]>(`/events?lang=${language}&category=${category}`);
    return response.data;
  },
};

// Weather API - Vedur.is via our proxy
export const weatherApi = {
  // Get weather forecast for Reykjavik from our backend proxy
  getCurrent: async (): Promise<VedurWeatherResponse> => {
    try {
      const params = new URLSearchParams();
      Object.entries(WEATHER_API.params).forEach(([key, value]) => {
        params.append(key, String(value));
      });

      // Use /weather endpoint (client already has /api base)
      const response = await client.get<VedurWeatherResponse>(
        `/weather?${params.toString()}`
      );

      return response.data;
    } catch (error) {
      console.error('Weather API error:', error);
      throw error;
    }
  },
};

// Currency API - exchangerate-api.com (free tier, no key required)
export const currencyApi = {
  // Get current exchange rates
  getRates: async (baseCurrency: Currency = 'ISK') => {
    try {
      const url = `${CURRENCY_API.baseUrl}/${baseCurrency}`;
      const response = await externalClient.get<OpenExchangeRatesResponse>(url);
      return response.data;
    } catch (error) {
      console.error('Currency API error:', error);
      throw error;
    }
  },

  // Get historical exchange rates for a date range
  getHistoricalRates: async (
    baseCurrency: Currency = 'USD',
    targetCurrency: Currency = 'ISK',
    startDate?: string,
    endDate?: string
  ) => {
    try {
      // Default to last 30 days if dates not provided
      const end = endDate || new Date().toISOString().split('T')[0];
      const start = startDate || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
        .toISOString().split('T')[0];

      const url = `${HISTORICAL_CURRENCY_API.baseUrl}/${start}..${end}`;
      const response = await externalClient.get(url, {
        params: {
          base: baseCurrency,
          symbols: targetCurrency,
        },
      });

      return response.data;
    } catch (error) {
      console.error('Historical Currency API error:', error);
      throw error;
    }
  },
};

// Word of the Day API
export const wordOfDayApi = {
  // Get today's word
  getToday: async (language: Language): Promise<WordOfDay> => {
    const response = await client.get<WordOfDay>(`/word-of-day?lang=${language}`);
    return response.data;
  },
};

// Air Quality API - Iceland Environment Agency (UST)
export const airQualityApi = {
  // Get latest air quality readings from all stations
  getLatest: async () => {
    try {
      const response = await externalClient.get(AIR_QUALITY_API.baseUrl);
      return response.data;
    } catch (error) {
      console.error('Air Quality API error:', error);
      throw error;
    }
  },
};

// Holidays API - Nager.Date
export const holidaysApi = {
  // Get public holidays for a specific year
  getByYear: async (year: number) => {
    try {
      const url = `${HOLIDAYS_API.baseUrl}/${year}/${HOLIDAYS_API.countryCode}`;
      const response = await externalClient.get(url);
      return response.data;
    } catch (error) {
      console.error('Holidays API error:', error);
      throw error;
    }
  },
};

export default client;
